import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Image as ImageIcon, MessageCircle } from "lucide-react";
import { getWhatsAppUrl, WHATSAPP_DISPLAY } from "@/lib/whatsapp";
import { trackEvent } from "@/lib/tracking";

export const RYDE_EASE = [0.22, 1, 0.36, 1];

export const RydeFade = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay, ease: RYDE_EASE }}
    className={className}
  >
    {children}
  </motion.div>
);

export const SectionTag = ({ children, testId }) => (
  <p className="font-jbmono text-xs uppercase tracking-[0.3em] text-[#C6FF3D]" data-testid={testId}>
    {children}
  </p>
);

export const BentoCard = ({ children, className = "", ...rest }) => (
  <div
    className={`rounded-2xl border border-[#2A322E] bg-[#141816] p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] md:p-8 ${className}`}
    {...rest}
  >
    {children}
  </div>
);

export const RydeCta = ({ to, href, children, testId, className = "" }) => {
  const cls = `group inline-flex min-h-[52px] items-center justify-center gap-2 rounded-xl bg-[#C6FF3D] px-7 py-4 font-display text-base font-extrabold tracking-tight text-[#0B0D0C] transition-colors duration-200 hover:bg-[#D4FF6B] focus:outline-none focus:ring-2 focus:ring-[#C6FF3D] focus:ring-offset-2 focus:ring-offset-[#0B0D0C] active:scale-95 ${className}`;
  const inner = (
    <>
      {children}
      <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
    </>
  );
  if (href) {
    return <a href={href} className={cls} data-testid={testId}>{inner}</a>;
  }
  return <Link to={to || "/"} className={cls} data-testid={testId}>{inner}</Link>;
};

export const GhostButton = ({ href, to, children, testId, className = "" }) => {
  const cls = `inline-flex min-h-[52px] items-center justify-center gap-2 rounded-xl border border-[#2A322E] bg-[#141816] px-7 py-4 font-display text-base font-bold text-[#F4F7F5] transition-colors duration-200 hover:border-[#C6FF3D]/50 active:scale-95 ${className}`;
  if (href) return <a href={href} className={cls} data-testid={testId}>{children}</a>;
  return <Link to={to || "/"} className={cls} data-testid={testId}>{children}</Link>;
};

export const WhatsAppButton = ({ source, label = "WhatsApp karo", message, testId, className = "" }) => (
  <a
    href={getWhatsAppUrl(message)}
    target="_blank"
    rel="noreferrer"
    onClick={() => trackEvent("whatsapp_click", source)}
    className={`inline-flex min-h-[52px] items-center justify-center gap-2 rounded-xl border border-[#25D366]/40 bg-[#25D366]/10 px-6 py-4 font-display text-base font-bold text-[#25D366] transition-colors duration-200 hover:bg-[#25D366]/20 active:scale-95 ${className}`}
    data-testid={testId}
  >
    <MessageCircle size={18} />
    {label}
  </a>
);

export const RydeWhatsAppFab = () => (
  <a
    href={getWhatsAppUrl("I want Slate 01 booking. City:")}
    target="_blank"
    rel="noreferrer"
    onClick={() => trackEvent("whatsapp_click", "fab")}
    className="group fixed bottom-6 right-4 z-[60] flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-black shadow-[0_8px_30px_rgba(37,211,102,0.35)] transition-colors duration-200 hover:bg-[#1DA851] active:scale-95 md:right-6"
    aria-label={`Chat on WhatsApp ${WHATSAPP_DISPLAY}`}
    data-testid="whatsapp-fab"
  >
    <span className="absolute inset-0 animate-ping rounded-full bg-[#25D366] opacity-20" />
    <MessageCircle size={24} className="relative" />
  </a>
);

export const RydeWordmark = ({ testId = "ryde-wordmark" }) => (
  <Link to="/" className="font-display text-2xl font-black tracking-tight" data-testid={testId}>
    <span className="text-[#F4F7F5]">RYDE</span><span className="text-[#C6FF3D]">CAST</span>
  </Link>
);

export const RydeNav = ({ minimal = false }) => (
  <nav className="fixed inset-x-0 top-0 z-50 border-b border-[#2A322E] bg-[#0B0D0C]/85 backdrop-blur-xl" data-testid="ryde-nav">
    <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 md:px-8">
      <RydeWordmark testId="ryde-nav-logo" />
      {!minimal && (
        <div className="hidden items-center gap-8 font-jbmono text-xs uppercase tracking-[0.18em] text-[#9AA8A0] md:flex">
          <a href="/#kit" className="transition-colors duration-200 hover:text-[#F4F7F5]" data-testid="ryde-nav-kit">The Kit</a>
          <a href="/#pricing" className="transition-colors duration-200 hover:text-[#F4F7F5]" data-testid="ryde-nav-pricing">Pricing</a>
          <a href="/#faq" className="transition-colors duration-200 hover:text-[#F4F7F5]" data-testid="ryde-nav-faq">Sawaal</a>
          <Link to="/world" className="transition-colors duration-200 hover:text-[#F4F7F5]" data-testid="ryde-nav-world">Network</Link>
          <Link to="/track" className="transition-colors duration-200 hover:text-[#F4F7F5]" data-testid="ryde-nav-track">Track order</Link>
        </div>
      )}
      <div className="flex items-center gap-3">
        <a
          href={getWhatsAppUrl("I want Slate 01 booking. City:")}
          target="_blank"
          rel="noreferrer"
          onClick={() => trackEvent("whatsapp_click", "nav")}
          className="hidden items-center gap-2 rounded-xl border border-[#25D366]/40 px-4 py-2.5 font-display text-sm font-bold text-[#25D366] transition-colors duration-200 hover:bg-[#25D366]/10 sm:inline-flex"
          data-testid="ryde-nav-whatsapp"
        >
          <MessageCircle size={16} />
          WhatsApp
        </a>
        <a
          href="/#order"
          className="rounded-xl bg-[#C6FF3D] px-5 py-2.5 font-display text-sm font-extrabold text-[#0B0D0C] transition-colors duration-200 hover:bg-[#D4FF6B] active:scale-95"
          data-testid="ryde-nav-cta"
        >
          Get Slate 01
        </a>
      </div>
    </div>
  </nav>
);

export const ImgPlaceholder = ({ label, note, ratio = "aspect-[4/3]", testId }) => (
  <div
    className={`relative flex ${ratio} w-full items-center justify-center overflow-hidden rounded-2xl border border-dashed border-[#C6FF3D]/35 bg-[#141816]`}
    data-testid={testId}
  >
    <div
      className="pointer-events-none absolute inset-0 opacity-[0.06]"
      style={{
        backgroundImage:
          "repeating-linear-gradient(135deg, #C6FF3D 0px, #C6FF3D 1px, transparent 1px, transparent 18px)",
      }}
    />
    <div className="relative px-6 text-center">
      <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl border border-[#2A322E] bg-[#0B0D0C]">
        <ImageIcon size={20} className="text-[#C6FF3D]" />
      </span>
      <p className="mt-4 font-jbmono text-xs uppercase tracking-[0.2em] text-[#C6FF3D]">{label}</p>
      {note && <p className="mt-2 max-w-xs text-xs leading-5 text-[#9AA8A0]">{note}</p>}
    </div>
  </div>
);

const IndiaFlag = () => (
  <svg width="22" height="15" viewBox="0 0 22 15" className="rounded-[2px]" aria-label="Indian flag" data-testid="footer-flag">
    <rect width="22" height="5" fill="#FF9933" />
    <rect y="5" width="22" height="5" fill="#FFFFFF" />
    <rect y="10" width="22" height="5" fill="#138808" />
    <circle cx="11" cy="7.5" r="1.7" fill="none" stroke="#000080" strokeWidth="0.6" />
  </svg>
);

export const RydeFooter = () => (
  <footer className="border-t border-[#2A322E] bg-[#0B0D0C] px-5 py-14 md:px-8" data-testid="ryde-footer">
    <div className="mx-auto grid max-w-6xl gap-10 md:grid-cols-3">
      <div>
        <p className="font-display text-2xl font-black tracking-tight">
          <span className="text-[#F4F7F5]">RYDE</span><span className="text-[#C6FF3D]">CAST</span>
        </p>
        <p className="mt-1 font-jbmono text-xs uppercase tracking-[0.2em] text-[#9AA8A0]">Slate 01 · India</p>
        <p className="mt-4 flex items-center gap-2.5 text-sm text-[#9AA8A0]" data-testid="footer-address">
          <IndiaFlag />
          Delhi, India
        </p>
      </div>
      <div className="flex flex-col gap-3 font-jbmono text-xs uppercase tracking-[0.18em] text-[#9AA8A0]">
        <a href="/#kit" className="transition-colors duration-200 hover:text-[#F4F7F5]" data-testid="ryde-footer-kit">The Kit</a>
        <Link to="/world" className="transition-colors duration-200 hover:text-[#F4F7F5]" data-testid="ryde-footer-world">The Network</Link>
        <Link to="/track" className="transition-colors duration-200 hover:text-[#F4F7F5]" data-testid="ryde-footer-track">Track Order</Link>
        <Link to="/refunds" className="transition-colors duration-200 hover:text-[#F4F7F5]" data-testid="ryde-footer-refunds">Refund Policy</Link>
        <Link to="/privacy" className="transition-colors duration-200 hover:text-[#F4F7F5]" data-testid="ryde-footer-privacy">Privacy</Link>
        <Link to="/terms" className="transition-colors duration-200 hover:text-[#F4F7F5]" data-testid="ryde-footer-terms">Terms</Link>
      </div>
      <div className="text-sm leading-6 text-[#9AA8A0]">
        <p className="font-jbmono text-xs uppercase tracking-[0.18em] text-[#9AA8A0]">Seedhi baat</p>
        <p className="mt-3">
          Delivery in 15–20 days. COD available. Hardware warranty as stated. Ad earnings not guaranteed. Android tablet, not Apple.
        </p>
      </div>
    </div>

    <div className="mx-auto mt-12 max-w-6xl rounded-2xl border border-[#2A322E] bg-[#141816] p-5 md:p-6" data-testid="footer-thanks">
      <div className="flex items-center gap-4">
        <img
          src="/images/modi-portrait.jpg"
          alt="Shri Narendra Modi, Prime Minister of India"
          className="h-14 w-14 rounded-full border border-[#2A322E] object-cover"
          data-testid="footer-modi-photo"
        />
        <div>
          <p className="font-display text-base font-extrabold text-[#F4F7F5]">
            Built in Delhi. Grateful for Startup India — thank you, Modi ji.
          </p>
          <p className="mt-1 text-xs leading-5 text-[#9AA8A0]">
            A small Indian startup trying to turn everyday drives into income.
          </p>
        </div>
      </div>
      <p className="mt-3 text-[10px] leading-4 text-[#9AA8A0]/70">
        Photo: Prime Minister’s Office (GODL-India), via Wikimedia Commons. A note of thanks — not an endorsement.
      </p>
    </div>

    <p className="mx-auto mt-8 max-w-6xl border-t border-[#2A322E] pt-6 font-jbmono text-xs text-[#9AA8A0]">
      © 2026 RydeCast · Slate 01 · Delhi, India
    </p>
  </footer>
);
